import { type AxiosError } from 'axios'
import { useMutation, type UseMutationOptions } from 'react-query'

import agent from 'agent'
import useAuth from 'hooks/use-auth'
import { externalIntegrationsMutationKeys } from 'queries/external-integrations/query-keys'
import {
  type AuthenticationRequestBody,
  type CreateStatusCheckAuthenticationRequestParams
} from 'queries/external-integrations/types'
import { getEndpointWithParams } from 'queries/queries-utils'
import { type TeamExternalIntegrationResponse } from 'types/api/status-checks/status-check'

function createStatusCheckAuthentication([endpoint, endpointParams]: ReturnType<
  typeof externalIntegrationsMutationKeys.createStatusCheckAuthentication
>) {
  return async function (
    args: [
      CreateStatusCheckAuthenticationRequestParams,
      AuthenticationRequestBody
    ]
  ) {
    const [requestParams, requestBody] = args
    const endpointWithParams = getEndpointWithParams(endpoint, {
      ...endpointParams,
      ...requestParams
    })
    const response = await agent.post<TeamExternalIntegrationResponse>(
      endpointWithParams,
      requestBody
    )

    return response.data
  }
}

export function useCreateStatusCheckAuthentication(
  options?: UseMutationOptions<
    TeamExternalIntegrationResponse,
    AxiosError,
    [CreateStatusCheckAuthenticationRequestParams, AuthenticationRequestBody]
  >
) {
  const { currentTeamId: teamId } = useAuth()
  const mutationKey =
    externalIntegrationsMutationKeys.createStatusCheckAuthentication(teamId)

  return useMutation({
    ...options,
    mutationFn: createStatusCheckAuthentication(mutationKey),
    mutationKey
  })
}
